import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { PlusIcon } from 'lucide-react';

import { SharedSpinner } from '@codes/ui-kit';

import {
    OpenApiKeyCard,
    OpenApiKeyDeleteDialog,
    OpenApiKeyEmptyState,
    OpenApiKeyFormDialog,
    OpenApiKeyRegenerateDialog,
    OpenApiKeySuccessDialog,
    OpenApiKeyValidationDialog,
} from '../components';
import { useOpenApiKeys } from '../hooks';
import { maskApiKey } from '../utils';

export const OpenApiKeysPage = () => {
    const { t } = useTranslation();

    const {
        keysData,
        isKeysLoading,
        isKeysError,
        keysError,
        isFormDialogOpen,
        setIsFormDialogOpen,
        dialogMode,
        selectedKey,
        isSuccessDialogOpen,
        setIsSuccessDialogOpen,
        newlyCreatedKey,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,
        keyToDelete,
        isDeleting,
        isRegenerateDialogOpen,
        setIsRegenerateDialogOpen,
        keyToRegenerate,
        isRegenerating,
        isValidationDialogOpen,
        setIsValidationDialogOpen,
        keyToValidate,
        handleCreate,
        handleEdit,
        handleDelete,
        confirmDelete,
        handleFormSuccess,
        handleRegenerate,
        confirmRegenerate,
        handleValidate,
    } = useOpenApiKeys();

    if (isKeysLoading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <SharedSpinner />
            </div>
        );
    }

    if (isKeysError) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <div className="text-center">
                    <p className="text-destructive text-lg font-semibold mb-2">{t('openApiKeys.errorLoadingKeys')}</p>
                    <p className="text-muted-foreground text-sm">{keysError?.message || t('common.unexpectedError')}</p>
                </div>
            </div>
        );
    }

    const keys = keysData?.list || [];

    return (
        <div className="flex-1 bg-background min-h-0 w-full flex flex-col">
            <div className="flex-1 py-[34px] px-[68px] max-md:px-4 max-md:pt-6">
                <div className="mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-2xl font-bold">{t('openApiKeys.title')}</h1>
                        <Button onClick={handleCreate}>
                            <PlusIcon className="mr-2 h-4 w-4" />
                            {t('openApiKeys.createNew')}
                        </Button>
                    </div>

                    {keys.length === 0 ? (
                        <OpenApiKeyEmptyState onCreateClick={handleCreate} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {keys.map(key => (
                                <OpenApiKeyCard
                                    key={key.id || key.apiKey}
                                    data={key}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onRegenerate={handleRegenerate}
                                    onValidate={handleValidate}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <OpenApiKeyFormDialog
                open={isFormDialogOpen}
                onOpenChange={setIsFormDialogOpen}
                mode={dialogMode}
                keyData={selectedKey}
                onSuccess={handleFormSuccess}
            />

            {newlyCreatedKey && (
                <OpenApiKeySuccessDialog
                    open={isSuccessDialogOpen}
                    onOpenChange={setIsSuccessDialogOpen}
                    apiKey={newlyCreatedKey.apiKey}
                    keyName={newlyCreatedKey.name}
                />
            )}

            {keyToDelete && (
                <OpenApiKeyDeleteDialog
                    open={isDeleteDialogOpen}
                    onOpenChange={setIsDeleteDialogOpen}
                    keyName={keyToDelete.name || keyToDelete.id || 'key'}
                    isDeleting={isDeleting}
                    onConfirm={confirmDelete}
                />
            )}

            {keyToRegenerate && (
                <OpenApiKeyRegenerateDialog
                    open={isRegenerateDialogOpen}
                    onOpenChange={setIsRegenerateDialogOpen}
                    keyName={keyToRegenerate.name || keyToRegenerate.id || 'key'}
                    maskedKey={maskApiKey(keyToRegenerate.apiKey || '')}
                    isRegenerating={isRegenerating}
                    onConfirm={confirmRegenerate}
                />
            )}

            <OpenApiKeyValidationDialog
                open={isValidationDialogOpen}
                onOpenChange={setIsValidationDialogOpen}
                keyToValidate={keyToValidate}
            />
        </div>
    );
};
